import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, TextInput, TouchableOpacity, ScrollView, Alert, Platform, Linking } from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import AnimatedCard from './AnimatedCard';
import { fileService } from '../services/fileService';
import { aiService } from '../services/aiService';
import { StrategicDecisionMatrix, StrategicActionMatrix, TigerTeamBreakoutsTracker } from './PersistentWidgets';

/**
 * WidgetRenderer - Displays interactive widgets (spreadsheets, quizzes, files) on the frontend
 * @param {Object} widget - The widget configuration from Firestore
 * @param {Function} onComplete - Callback when widget is completed (optional)
 * @param {boolean} compact - Whether to render in compact mode (for sidebar)
 */
const WidgetRenderer = ({ widget, onComplete, compact = false, reviewMode = false }) => {
  const { palette } = useTheme();

  if (!widget) return null;

  // Render based on widget type
  if (widget.type === 'spreadsheet') {
    return <SpreadsheetWidget widget={widget} palette={palette} compact={compact} />;
  } else if (widget.type === 'quiz') {
    return <QuizWidget widget={widget} palette={palette} onComplete={onComplete} compact={compact} />;
  } else if (widget.type === 'file') {
    return <FileWidget widget={widget} palette={palette} compact={compact} />;
  } else if (widget.type === 'matching') {
    return <MatchingWidget widget={widget} palette={palette} onComplete={onComplete} compact={compact} />;
  } else if (widget.type === 'calculator') {
    return <CalculatorWidget widget={widget} palette={palette} compact={compact} />;
  } else if (widget.type === 'scorecard') {
    return <ScorecardWidget widget={widget} palette={palette} onComplete={onComplete} compact={compact} />;
  } else if (widget.type === 'checklist') {
    return <ChecklistWidget widget={widget} palette={palette} onComplete={onComplete} compact={compact} />;
  } else if (widget.type === 'decision_tree') {
    return <DecisionTreeWidget widget={widget} palette={palette} onComplete={onComplete} compact={compact} />;
  } else if (widget.type === 'timeline') {
    return <TimelineWidget widget={widget} palette={palette} compact={compact} />;
  } else if (widget.type === 'comparison') {
    return <ComparisonWidget widget={widget} palette={palette} compact={compact} />;
  } else if (widget.type === 'sequential_quiz') {
    return <SequentialQuizWidget widget={widget} palette={palette} onComplete={onComplete} compact={compact} />;
  } else if (widget.type === 'strategic_decision') {
    return <StrategicDecisionMatrix widget={widget} palette={palette} onComplete={onComplete} compact={compact} reviewMode={reviewMode} />;
  } else if (widget.type === 'strategic_action') {
    return <StrategicActionMatrix widget={widget} palette={palette} onComplete={onComplete} compact={compact} reviewMode={reviewMode} />;
  } else if (widget.type === 'tiger_team_breakouts') {
    return <TigerTeamBreakoutsTracker widget={widget} palette={palette} onComplete={onComplete} compact={compact} reviewMode={reviewMode} />;
  }

  // Fallback for unrecognized widget types - show basic info
  console.log('Unrecognized widget type:', widget.type, widget);
  return (
    <AnimatedCard style={[styles.widgetCard, { backgroundColor: palette.surface, borderColor: palette.border || 'rgba(255,255,255,0.16)' }]}>
      <View style={styles.widgetHeader}>
        <Text style={[styles.widgetTitle, { color: palette.text || 'white' }]}>
          🧩 {widget.title || 'Untitled Widget'}
        </Text>
        {widget.description && (
          <Text style={[styles.widgetDescription, { color: palette.mutedText || 'rgba(255,255,255,0.7)' }]}>
            {widget.description}
          </Text>
        )}
        <Text style={[styles.widgetDescription, { color: palette.mutedText || 'rgba(255,255,255,0.5)', marginTop: 8, fontSize: 12 }]}>
          Type: {widget.type || 'unknown'}
        </Text>
      </View>
    </AnimatedCard>
  );
};

// Spreadsheet Widget Component
const SpreadsheetWidget = ({ widget, palette, compact = false }) => {
  const config = widget.config || {};
  const columns = config.columns || [];
  const initialRows = config.rows || 5;

  // Initialize spreadsheet data
  const [rows, setRows] = useState(() => {
    const data = [];
    for (let i = 0; i < initialRows; i++) {
      const row = {};
      columns.forEach((col) => {
        row[col.name] = '';
      });
      data.push(row);
    }
    return data;
  });

  const handleCellChange = (rowIndex, columnName, value) => {
    const newRows = [...rows];
    newRows[rowIndex][columnName] = value;
    setRows(newRows);
  };

  const handleAddRow = () => {
    const newRow = {};
    columns.forEach((col) => {
      newRow[col.name] = '';
    });
    setRows([...rows, newRow]);
  };

  const handleRemoveRow = (rowIndex) => {
    if (rows.length > 1) {
      const newRows = rows.filter((_, index) => index !== rowIndex);
      setRows(newRows);
    }
  };

  return (
    <AnimatedCard style={[styles.widgetCard, { backgroundColor: palette.surface, borderColor: palette.border || 'rgba(255,255,255,0.16)' }]}>
      <View style={styles.widgetHeader}>
        <Text style={[styles.widgetTitle, { color: palette.text || 'white' }]}>
          📊 {widget.title}
        </Text>
        {widget.description && (
          <Text style={[styles.widgetDescription, { color: palette.mutedText || 'rgba(255,255,255,0.7)' }]}>
            {widget.description}
          </Text>
        )}
      </View>

      <ScrollView horizontal style={styles.spreadsheetScroll}>
        <View style={styles.spreadsheetContainer}>
          {/* Header Row */}
          <View style={[styles.spreadsheetRow, styles.spreadsheetHeaderRow, { borderBottomColor: palette.border || 'rgba(255,255,255,0.2)' }]}>
            <View style={styles.rowNumberCell}>
              <Text style={[styles.columnHeader, { color: palette.mutedText }]}>#</Text>
            </View>
            {columns.map((column, colIndex) => (
              <View key={colIndex} style={styles.spreadsheetCell}>
                <Text style={[styles.columnHeader, { color: palette.text || 'white' }]}>
                  {column.name}
                </Text>
                <Text style={[styles.columnType, { color: palette.mutedText }]}>
                  ({column.type})
                </Text>
              </View>
            ))}
            <View style={styles.actionCell} />
          </View>

          {/* Data Rows */}
          {rows.map((row, rowIndex) => (
            <View key={rowIndex} style={[styles.spreadsheetRow, { borderBottomColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
              <View style={styles.rowNumberCell}>
                <Text style={[styles.rowNumber, { color: palette.mutedText }]}>{rowIndex + 1}</Text>
              </View>
              {columns.map((column, colIndex) => (
                <View key={colIndex} style={styles.spreadsheetCell}>
                  <TextInput
                    style={[styles.cellInput, { color: palette.text || 'white', borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}
                    value={row[column.name]}
                    onChangeText={(value) => handleCellChange(rowIndex, column.name, value)}
                    placeholder={column.type === 'currency' ? '$0.00' : column.type === 'number' ? '0' : 'Enter ' + column.name}
                    placeholderTextColor={palette.mutedText || 'rgba(255,255,255,0.3)'}
                    keyboardType={column.type === 'number' || column.type === 'currency' ? 'numeric' : 'default'}
                  />
                </View>
              ))}
              <View style={styles.actionCell}>
                {rows.length > 1 && (
                  <TouchableOpacity onPress={() => handleRemoveRow(rowIndex)} style={styles.removeRowButton}>
                    <Text style={styles.removeRowButtonText}>✕</Text>
                  </TouchableOpacity>
                )}
              </View>
            </View>
          ))}
        </View>
      </ScrollView>

      <TouchableOpacity onPress={handleAddRow} style={[styles.addRowButton, { borderColor: palette.primary || '#FF0600' }]}>
        <Text style={[styles.addRowButtonText, { color: palette.primary || '#FF0600' }]}>+ Add Row</Text>
      </TouchableOpacity>
    </AnimatedCard>
  );
};

// Quiz Widget Component
const QuizWidget = ({ widget, palette, onComplete, compact = false }) => {
  const config = widget.config || {};
  const questions = config.questions || [];
  
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [score, setScore] = useState(0);

  const handleSelectAnswer = (questionIndex, answerIndex) => {
    if (submitted) return; // Can't change after submission
    
    setAnswers({
      ...answers,
      [questionIndex]: answerIndex,
    });
  };

  const handleSubmit = () => {
    let correctCount = 0;
    questions.forEach((question, qIndex) => {
      if (answers[qIndex] === question.correctAnswer) {
        correctCount++;
      }
    });

    setScore(correctCount);
    setSubmitted(true);

    const percentage = (correctCount / questions.length) * 100;
    Alert.alert(
      'Quiz Complete!',
      `You scored ${correctCount} out of ${questions.length} (${percentage.toFixed(0)}%)`
    );

    if (onComplete) {
      onComplete({ score: correctCount, total: questions.length, percentage });
    }
  };

  const handleRetry = () => {
    setAnswers({});
    setSubmitted(false);
    setScore(0);
  };

  return (
    <AnimatedCard style={[styles.widgetCard, { backgroundColor: palette.surface, borderColor: palette.border || 'rgba(255,255,255,0.16)' }]}>
      <View style={styles.widgetHeader}>
        <Text style={[styles.widgetTitle, { color: palette.text || 'white' }]}>
          🎯 {widget.title}
        </Text>
        {widget.description && (
          <Text style={[styles.widgetDescription, { color: palette.mutedText || 'rgba(255,255,255,0.7)' }]}>
            {widget.description}
          </Text>
        )}
      </View>

      {questions.map((question, qIndex) => {
        const userAnswer = answers[qIndex];
        const isCorrect = submitted && userAnswer === question.correctAnswer;
        const isWrong = submitted && userAnswer !== undefined && userAnswer !== question.correctAnswer;

        return (
          <View key={qIndex} style={[styles.quizQuestion, { borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
            <Text style={[styles.questionText, { color: palette.text || 'white' }]}>
              {qIndex + 1}. {question.question}
            </Text>

            {question.answers.map((answer, aIndex) => {
              const isSelected = userAnswer === aIndex;
              const isCorrectAnswer = submitted && aIndex === question.correctAnswer;

              return (
                <TouchableOpacity
                  key={aIndex}
                  style={[
                    styles.answerOption,
                    { borderColor: palette.border || 'rgba(255,255,255,0.2)' },
                    isSelected && !submitted && { backgroundColor: 'rgba(255, 6, 0, 0.15)', borderColor: palette.primary },
                    isCorrectAnswer && { backgroundColor: 'rgba(13, 196, 74, 0.15)', borderColor: '#0DC44A' },
                    isSelected && isWrong && { backgroundColor: 'rgba(234, 128, 107, 0.15)', borderColor: '#EA806B' },
                  ]}
                  onPress={() => handleSelectAnswer(qIndex, aIndex)}
                  disabled={submitted}
                >
                  <View style={[styles.answerRadio, { borderColor: palette.border || 'rgba(255,255,255,0.3)' }]}>
                    {isSelected && !submitted && <View style={[styles.answerRadioSelected, { backgroundColor: palette.primary }]} />}
                    {isCorrectAnswer && <Text style={styles.correctIcon}>✓</Text>}
                    {isSelected && isWrong && <Text style={styles.wrongIcon}>✕</Text>}
                  </View>
                  <Text style={[styles.answerText, { color: palette.text || 'white' }]}>
                    {answer}
                  </Text>
                </TouchableOpacity>
              );
            })}
          </View>
        );
      })}

      {submitted ? (
        <View style={styles.quizResults}>
          <Text style={[styles.quizScore, { color: palette.text || 'white' }]}>
            Score: {score} / {questions.length} ({((score / questions.length) * 100).toFixed(0)}%)
          </Text>
          <TouchableOpacity onPress={handleRetry} style={[styles.retryButton, { backgroundColor: palette.primary || '#FF0600' }]}>
            <Text style={styles.retryButtonText}>Try Again</Text>
          </TouchableOpacity>
        </View>
      ) : (
        <TouchableOpacity
          onPress={handleSubmit}
          style={[styles.submitButton, { backgroundColor: palette.primary || '#FF0600' }]}
          disabled={Object.keys(answers).length < questions.length}
        >
          <Text style={styles.submitButtonText}>
            Submit Quiz ({Object.keys(answers).length}/{questions.length} answered)
          </Text>
        </TouchableOpacity>
      )}
    </AnimatedCard>
  );
};

// Matching Game Widget Component
const MatchingWidget = ({ widget, palette, onComplete, compact = false }) => {
  const config = widget.config || {};
  const pairs = config.pairs || [];

  // Shuffle function
  const shuffleArray = (array) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  // Initialize shuffled items
  const [leftItems] = useState(() => 
    shuffleArray(pairs.map((pair, index) => ({ ...pair, id: index })))
  );
  const [rightItems] = useState(() => 
    shuffleArray(pairs.map((pair, index) => ({ ...pair, id: index })))
  );

  // State management
  const [selectedLeft, setSelectedLeft] = useState(null);
  const [selectedRight, setSelectedRight] = useState(null);
  const [matches, setMatches] = useState([]); // Array of { leftId, rightId }
  const [completed, setCompleted] = useState(false);
  const [showResults, setShowResults] = useState(false);

  const handleItemClick = (item, side) => {
    // Don't allow clicking already matched items
    if (matches.some(m => m.leftId === item.id || m.rightId === item.id)) {
      return;
    }

    if (side === 'left') {
      if (selectedLeft?.id === item.id) {
        setSelectedLeft(null); // Deselect
      } else {
        setSelectedLeft(item);
        // Check if we can make a match
        if (selectedRight) {
          handleMatch(item, selectedRight);
        }
      }
    } else {
      if (selectedRight?.id === item.id) {
        setSelectedRight(null); // Deselect
      } else {
        setSelectedRight(item);
        // Check if we can make a match
        if (selectedLeft) {
          handleMatch(selectedLeft, item);
        }
      }
    }
  };

  const handleMatch = (leftItem, rightItem) => {
    const isCorrect = leftItem.id === rightItem.id;
    
    if (isCorrect) {
      // Correct match
      const newMatches = [...matches, { leftId: leftItem.id, rightId: rightItem.id, correct: true }];
      setMatches(newMatches);
      setSelectedLeft(null);
      setSelectedRight(null);

      // Check if all pairs are matched
      if (newMatches.length === pairs.length) {
        setCompleted(true);
        setShowResults(true);
        if (onComplete) {
          onComplete({ score: pairs.length, total: pairs.length, percentage: 100 });
        }
      }
    } else {
      // Incorrect match - show briefly then deselect
      setTimeout(() => {
        setSelectedLeft(null);
        setSelectedRight(null);
      }, 500);
    }
  };

  const handleReset = () => {
    setMatches([]);
    setSelectedLeft(null);
    setSelectedRight(null);
    setCompleted(false);
    setShowResults(false);
  };

  const isMatched = (itemId) => {
    return matches.some(m => m.leftId === itemId || m.rightId === itemId);
  };

  const isSelected = (item, side) => {
    return side === 'left' ? selectedLeft?.id === item.id : selectedRight?.id === item.id;
  };

  return (
    <AnimatedCard style={[styles.widgetCard, { backgroundColor: palette.surface, borderColor: palette.border || 'rgba(255,255,255,0.16)' }]}>
      <View style={styles.widgetHeader}>
        <Text style={[styles.widgetTitle, { color: palette.text || 'white' }]}>
          🎯 {widget.title}
        </Text>
        {widget.description && (
          <Text style={[styles.widgetDescription, { color: palette.mutedText || 'rgba(255,255,255,0.7)' }]}>
            {widget.description}
          </Text>
        )}
      </View>

      {!showResults ? (
        <>
          <Text style={[styles.matchingInstructions, { color: palette.mutedText }]}>
            Click items from both columns to match them together
          </Text>

          <View style={styles.matchingContainer}>
            {/* Left Column */}
            <View style={styles.matchingColumn}>
              {leftItems.map((item, index) => {
                const matched = isMatched(item.id);
                const selected = isSelected(item, 'left');

                return (
                  <TouchableOpacity
                    key={index}
                    style={[
                      styles.matchingItem,
                      { borderColor: palette.border || 'rgba(255,255,255,0.2)' },
                      matched && { backgroundColor: 'rgba(13, 196, 74, 0.15)', borderColor: '#0DC44A' },
                      selected && !matched && { backgroundColor: 'rgba(255, 6, 0, 0.15)', borderColor: palette.primary },
                    ]}
                    onPress={() => handleItemClick(item, 'left')}
                    disabled={matched}
                  >
                    <Text style={[styles.matchingItemText, { color: matched ? '#0DC44A' : palette.text }]}>
                      {item.left}
                    </Text>
                    {matched && (
                      <Text style={{ color: '#0DC44A', fontSize: 16, marginLeft: 8 }}>✓</Text>
                    )}
                  </TouchableOpacity>
                );
              })}
            </View>

            {/* Right Column */}
            <View style={styles.matchingColumn}>
              {rightItems.map((item, index) => {
                const matched = isMatched(item.id);
                const selected = isSelected(item, 'right');

                return (
                  <TouchableOpacity
                    key={index}
                    style={[
                      styles.matchingItem,
                      { borderColor: palette.border || 'rgba(255,255,255,0.2)' },
                      matched && { backgroundColor: 'rgba(13, 196, 74, 0.15)', borderColor: '#0DC44A' },
                      selected && !matched && { backgroundColor: 'rgba(255, 6, 0, 0.15)', borderColor: palette.primary },
                    ]}
                    onPress={() => handleItemClick(item, 'right')}
                    disabled={matched}
                  >
                    <Text style={[styles.matchingItemText, { color: matched ? '#0DC44A' : palette.text }]}>
                      {item.right}
                    </Text>
                    {matched && (
                      <Text style={{ color: '#0DC44A', fontSize: 16, marginLeft: 8 }}>✓</Text>
                    )}
                  </TouchableOpacity>
                );
              })}
            </View>
          </View>

          <View style={styles.matchingProgress}>
            <Text style={[styles.matchingProgressText, { color: palette.mutedText }]}>
              Matched: {matches.length} / {pairs.length}
            </Text>
          </View>
        </>
      ) : (
        <View style={styles.matchingResults}>
          <Text style={[styles.matchingResultTitle, { color: '#0DC44A' }]}>
            🎉 Perfect Match!
          </Text>
          <Text style={[styles.matchingResultText, { color: palette.text }]}>
            You matched all {pairs.length} pairs correctly!
          </Text>
          <TouchableOpacity
            onPress={handleReset}
            style={[styles.retryButton, { backgroundColor: palette.primary || '#FF0600' }]}
          >
            <Text style={styles.retryButtonText}>Play Again</Text>
          </TouchableOpacity>
        </View>
      )}
    </AnimatedCard>
  );
};

const styles = StyleSheet.create({
  widgetCard: {
    marginVertical: 16,
    padding: 20,
    borderRadius: 16,
    borderWidth: 1,
  },
  widgetHeader: {
    marginBottom: 20,
  },
  widgetTitle: {
    fontSize: 20,
    fontFamily: 'Archivo',
    fontWeight: '700',
    marginBottom: 8,
  },
  widgetDescription: {
    fontSize: 14,
    fontFamily: 'Archivo',
    lineHeight: 20,
  },

  // Spreadsheet Styles
  spreadsheetScroll: {
    marginBottom: 16,
  },
  spreadsheetContainer: {
    minWidth: '100%',
  },
  spreadsheetRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
  },
  spreadsheetHeaderRow: {
    borderBottomWidth: 2,
  },
  rowNumberCell: {
    width: 40,
    padding: 10,
    justifyContent: 'center',
    alignItems: 'center',
  },
  rowNumber: {
    fontSize: 13,
    fontFamily: 'Archivo',
    fontWeight: '600',
  },
  spreadsheetCell: {
    width: 150,
    padding: 10,
  },
  columnHeader: {
    fontSize: 13,
    fontFamily: 'Archivo',
    fontWeight: '700',
    marginBottom: 2,
  },
  columnType: {
    fontSize: 11,
    fontFamily: 'Archivo',
  },
  cellInput: {
    padding: 8,
    borderWidth: 1,
    borderRadius: 6,
    fontSize: 14,
    fontFamily: 'Archivo',
  },
  actionCell: {
    width: 50,
    padding: 10,
    justifyContent: 'center',
    alignItems: 'center',
  },
  removeRowButton: {
    padding: 4,
  },
  removeRowButtonText: {
    color: '#EA806B',
    fontSize: 16,
  },
  addRowButton: {
    padding: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderStyle: 'dashed',
    alignItems: 'center',
  },
  addRowButtonText: {
    fontSize: 14,
    fontFamily: 'Archivo',
    fontWeight: '600',
  },

  // Quiz Styles
  quizQuestion: {
    marginBottom: 24,
    paddingBottom: 16,
    borderBottomWidth: 1,
  },
  questionText: {
    fontSize: 16,
    fontFamily: 'Archivo',
    fontWeight: '600',
    marginBottom: 12,
    lineHeight: 24,
  },
  answerOption: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
    borderRadius: 8,
    borderWidth: 1,
    marginBottom: 8,
  },
  answerRadio: {
    width: 20,
    height: 20,
    borderRadius: 10,
    borderWidth: 2,
    marginRight: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
  answerRadioSelected: {
    width: 10,
    height: 10,
    borderRadius: 5,
  },
  correctIcon: {
    color: '#0DC44A',
    fontSize: 14,
    fontWeight: '700',
  },
  wrongIcon: {
    color: '#EA806B',
    fontSize: 14,
    fontWeight: '700',
  },
  answerText: {
    fontSize: 14,
    fontFamily: 'Archivo',
    flex: 1,
  },
  submitButton: {
    padding: 16,
    borderRadius: 12,
    alignItems: 'center',
    marginTop: 8,
  },
  submitButtonText: {
    color: '#fff',
    fontSize: 16,
    fontFamily: 'Archivo',
    fontWeight: '700',
  },
  quizResults: {
    alignItems: 'center',
    marginTop: 16,
  },
  quizScore: {
    fontSize: 18,
    fontFamily: 'Archivo',
    fontWeight: '700',
    marginBottom: 12,
  },
  retryButton: {
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 8,
  },
  retryButtonText: {
    color: '#fff',
    fontSize: 14,
    fontFamily: 'Archivo',
    fontWeight: '600',
  },

  // File Widget Styles
  fileList: {
    gap: 12,
  },
  fileItem: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
    gap: 12,
    ...Platform.select({
      web: {
        cursor: 'pointer',
        transition: 'all 0.2s ease',
      },
    }),
  },
  fileItemHover: {
    transform: [{ scale: 1.02 }],
  },
  fileIcon: {
    width: 40,
    height: 40,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  fileInfo: {
    flex: 1,
  },
  fileName: {
    fontSize: 15,
    fontFamily: 'Archivo',
    fontWeight: '600',
    marginBottom: 4,
  },
  fileSize: {
    fontSize: 13,
    fontFamily: 'Archivo',
  },
  downloadIcon: {
    padding: 8,
  },
  uploadSection: {
    marginTop: 20,
    padding: 16,
    borderRadius: 12,
    borderWidth: 2,
    borderStyle: 'dashed',
    alignItems: 'center',
    gap: 12,
  },
  uploadButton: {
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderRadius: 8,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  uploadButtonText: {
    fontSize: 14,
    fontFamily: 'Archivo',
    fontWeight: '600',
  },
  uploadedFilesList: {
    marginTop: 16,
    gap: 8,
  },
  uploadedFileItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 12,
    borderRadius: 8,
    borderWidth: 1,
  },
  uploadedFileName: {
    fontSize: 14,
    fontFamily: 'Archivo',
    flex: 1,
  },
  removeFileButton: {
    padding: 4,
    marginLeft: 8,
  },
  feedbackContainer: {
    marginTop: 8,
    padding: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderLeftWidth: 4,
  },
  feedbackHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 8,
  },
  feedbackTitle: {
    fontSize: 14,
    fontFamily: 'Archivo',
    fontWeight: '700',
  },
  feedbackText: {
    fontSize: 14,
    fontFamily: 'Archivo',
    lineHeight: 20,
  },
  feedbackGenerating: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    padding: 12,
    borderRadius: 8,
    borderWidth: 1,
    marginTop: 12,
  },
  feedbackGeneratingText: {
    fontSize: 13,
    fontFamily: 'Archivo',
    fontStyle: 'italic',
  },
  getFeedbackButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 8,
    marginTop: 8,
    marginBottom: 8,
  },
  getFeedbackButtonText: {
    fontSize: 13,
    fontFamily: 'Archivo',
    fontWeight: '600',
  },
  emptyWidgetState: {
    padding: 24,
    borderRadius: 12,
    marginTop: 16,
    alignItems: 'center',
  },
  emptyWidgetText: {
    fontSize: 14,
    fontFamily: 'Archivo',
    textAlign: 'center',
    lineHeight: 20,
  },

  // Matching Game Styles
  matchingInstructions: {
    fontSize: 14,
    fontFamily: 'Archivo',
    textAlign: 'center',
    marginBottom: 20,
  },
  matchingContainer: {
    flexDirection: 'row',
    gap: 20,
    marginBottom: 16,
  },
  matchingColumn: {
    flex: 1,
    gap: 12,
  },
  matchingItem: {
    padding: 16,
    borderRadius: 12,
    borderWidth: 2,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    minHeight: 56,
    ...Platform.select({
      web: {
        cursor: 'pointer',
        transition: 'all 0.2s ease',
      },
    }),
  },
  matchingItemText: {
    fontSize: 15,
    fontFamily: 'Archivo',
    flex: 1,
  },
  matchingProgress: {
    alignItems: 'center',
    paddingVertical: 12,
  },
  matchingProgressText: {
    fontSize: 14,
    fontFamily: 'Archivo',
    fontWeight: '600',
  },
  matchingResults: {
    alignItems: 'center',
    paddingVertical: 40,
  },
  matchingResultTitle: {
    fontSize: 24,
    fontFamily: 'Archivo',
    fontWeight: '700',
    marginBottom: 12,
  },
  matchingResultText: {
    fontSize: 16,
    fontFamily: 'Archivo',
    marginBottom: 24,
    textAlign: 'center',
  },

  // Calculator Widget Styles
  calculatorContainer: {
    gap: 16,
  },
  calculatorField: {
    gap: 8,
  },
  calculatedResult: {
    padding: 16,
    borderRadius: 12,
    borderWidth: 2,
    alignItems: 'center',
  },
  calculatedResultText: {
    fontSize: 24,
    fontFamily: 'Archivo',
    fontWeight: '700',
  },

  // Scorecard Widget Styles
  scorecardCriterion: {
    paddingVertical: 20,
    borderBottomWidth: 1,
  },
  scorecardStatement: {
    fontSize: 15,
    fontFamily: 'Archivo',
    marginBottom: 12,
    lineHeight: 22,
  },
  scorecardScale: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  scoreButton: {
    width: 44,
    height: 44,
    borderRadius: 8,
    borderWidth: 2,
    alignItems: 'center',
    justifyContent: 'center',
    ...Platform.select({
      web: {
        cursor: 'pointer',
      },
    }),
  },
  scoreButtonText: {
    fontSize: 16,
    fontFamily: 'Archivo',
    fontWeight: '600',
  },
  scorecardResults: {
    alignItems: 'center',
    paddingVertical: 40,
  },
  scorecardResultTitle: {
    fontSize: 24,
    fontFamily: 'Archivo',
    fontWeight: '700',
    marginBottom: 12,
  },
  scorecardResultText: {
    fontSize: 16,
    fontFamily: 'Archivo',
    marginBottom: 24,
    textAlign: 'center',
  },

  // Checklist Widget Styles
  checklistProgress: {
    marginTop: 12,
    gap: 8,
  },
  checklistProgressText: {
    fontSize: 14,
    fontFamily: 'Archivo',
    fontWeight: '600',
  },
  progressBar: {
    width: '100%',
    height: 8,
    borderRadius: 4,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    borderRadius: 4,
  },
  checklistItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    paddingVertical: 16,
    borderBottomWidth: 1,
    gap: 12,
    ...Platform.select({
      web: {
        cursor: 'pointer',
      },
    }),
  },
  checkbox: {
    width: 24,
    height: 24,
    borderRadius: 6,
    borderWidth: 2,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 2,
  },
  checklistItemText: {
    fontSize: 15,
    fontFamily: 'Archivo',
    fontWeight: '600',
    lineHeight: 22,
  },
  checklistItemDescription: {
    fontSize: 13,
    fontFamily: 'Archivo',
    lineHeight: 18,
    marginTop: 4,
  },

  // Decision Tree Widget Styles
  decisionTreeContainer: {
    gap: 24,
  },
  decisionTreeNode: {
    padding: 24,
    borderRadius: 16,
    borderWidth: 1,
    gap: 20,
  },
  decisionTreeQuestion: {
    fontSize: 18,
    fontFamily: 'Archivo',
    fontWeight: '700',
    lineHeight: 26,
    textAlign: 'center',
  },
  decisionTreeButtons: {
    flexDirection: 'row',
    gap: 12,
  },
  decisionButton: {
    flex: 1,
    padding: 16,
    borderRadius: 12,
    alignItems: 'center',
    ...Platform.select({
      web: {
        cursor: 'pointer',
      },
    }),
  },
  decisionButtonText: {
    color: '#fff',
    fontSize: 16,
    fontFamily: 'Archivo',
    fontWeight: '700',
  },
  decisionPath: {
    gap: 8,
  },
  decisionPathTitle: {
    fontSize: 13,
    fontFamily: 'Archivo',
    fontWeight: '700',
    marginBottom: 4,
  },
  decisionPathStep: {
    fontSize: 13,
    fontFamily: 'Archivo',
    lineHeight: 18,
  },
  decisionTreeResult: {
    alignItems: 'center',
    paddingVertical: 40,
  },
  decisionTreeResultTitle: {
    fontSize: 24,
    fontFamily: 'Archivo',
    fontWeight: '700',
    marginBottom: 12,
  },
  decisionTreeResultText: {
    fontSize: 16,
    fontFamily: 'Archivo',
    marginBottom: 24,
    textAlign: 'center',
    lineHeight: 24,
  },

  // Timeline Widget Styles
  timelineContainer: {
    paddingLeft: 8,
  },
  timelineItem: {
    flexDirection: 'row',
    gap: 16,
  },
  timelineDot: {
    alignItems: 'center',
    paddingTop: 4,
  },
  timelineDotInner: {
    width: 16,
    height: 16,
    borderRadius: 8,
  },
  timelineLine: {
    width: 2,
    flex: 1,
    marginTop: 8,
    minHeight: 40,
  },
  timelineContent: {
    flex: 1,
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
    marginBottom: 24,
  },
  timelineMonth: {
    fontSize: 12,
    fontFamily: 'Archivo',
    fontWeight: '700',
    marginBottom: 4,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  timelineTitle: {
    fontSize: 16,
    fontFamily: 'Archivo',
    fontWeight: '700',
    marginBottom: 8,
  },
  timelineDescription: {
    fontSize: 14,
    fontFamily: 'Archivo',
    lineHeight: 20,
  },

  // Comparison Widget Styles
  comparisonScroll: {
    marginBottom: 16,
  },
  comparisonTable: {
    minWidth: '100%',
  },
  comparisonRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
  },
  comparisonCell: {
    width: 150,
    padding: 12,
    justifyContent: 'center',
  },
  comparisonHeaderCell: {
    backgroundColor: 'rgba(255,255,255,0.05)',
  },
  comparisonHeaderText: {
    fontSize: 14,
    fontFamily: 'Archivo',
    fontWeight: '700',
  },
  comparisonCriterionText: {
    fontSize: 14,
    fontFamily: 'Archivo',
    fontWeight: '600',
  },
  comparisonInput: {
    padding: 8,
    borderWidth: 1,
    borderRadius: 6,
    fontSize: 14,
    fontFamily: 'Archivo',
  },

  // Sequential Quiz Widget Styles
  sequentialQuizContainer: {
    gap: 20,
  },
  quizProgress: {
    gap: 8,
  },
  quizProgressText: {
    fontSize: 14,
    fontFamily: 'Archivo',
    fontWeight: '600',
  },
  sequentialQuestionCard: {
    padding: 24,
    borderRadius: 12,
    borderWidth: 1,
  },
  sequentialQuestionText: {
    fontSize: 18,
    fontFamily: 'Archivo',
    fontWeight: '600',
    lineHeight: 26,
  },
  sequentialOptionsContainer: {
    gap: 12,
  },
  sequentialOption: {
    padding: 16,
    borderRadius: 12,
    borderWidth: 2,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    ...Platform.select({
      web: {
        cursor: 'pointer',
        transition: 'all 0.2s ease',
      },
    }),
  },
  sequentialOptionText: {
    fontSize: 16,
    fontFamily: 'Archivo',
    fontWeight: '600',
    flex: 1,
  },
  sequentialFeedback: {
    alignItems: 'center',
    gap: 8,
  },
  sequentialFeedbackText: {
    fontSize: 18,
    fontFamily: 'Archivo',
    fontWeight: '700',
  },
  sequentialResults: {
    alignItems: 'center',
    paddingVertical: 40,
  },
  sequentialResultTitle: {
    fontSize: 28,
    fontFamily: 'Archivo',
    fontWeight: '700',
    marginBottom: 8,
  },
  sequentialResultPercentage: {
    fontSize: 20,
    fontFamily: 'Archivo',
    fontWeight: '600',
    marginBottom: 16,
  },
  sequentialResultText: {
    fontSize: 16,
    fontFamily: 'Archivo',
    textAlign: 'center',
  },

  // Shared styles
  label: {
    fontSize: 14,
    fontFamily: 'Archivo',
    fontWeight: '600',
    marginBottom: 8,
  },
  textInput: {
    padding: 12,
    borderWidth: 1,
    borderRadius: 8,
    fontSize: 14,
    fontFamily: 'Archivo',
  },
});

// File Widget Component
const FileWidget = ({ widget, palette, compact = false, moduleId = null, courseId = null }) => {
  const { user } = useAuth();
  const config = widget.config || {};
  const files = config.files || [];
  const allowUploads = config.allowUploads || false;
  const aiEnabled = config.aiEnabled || false;
  const aiPrompt = config.aiPrompt || '';
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [generatingFeedback, setGeneratingFeedback] = useState(false);
  const [feedback, setFeedback] = useState({});

  const handleDownload = async (file) => {
    try {
      if (Platform.OS === 'web') {
        window.open(file.url, '_blank');
      } else {
        await Linking.openURL(file.url);
      }
    } catch (error) {
      Alert.alert('Error', 'Failed to download file');
      console.error('Download error:', error);
    }
  };

  const handleFileUpload = async (event) => {
    if (!user) {
      Alert.alert('Error', 'You must be logged in to upload files');
      return;
    }

    const file = event.target.files[0];
    if (!file) return;

    // Validate file
    const validation = fileService.validateFile(file, {
      maxSize: 50 * 1024 * 1024, // 50MB
    });

    if (!validation.valid) {
      Alert.alert('Error', validation.error);
      return;
    }

    setUploading(true);

    try {
      const path = `user_uploads/${user.uid}/${widget.id}/${Date.now()}_${file.name}`;
      const { url, error } = await fileService.uploadFile(file, path);

      if (error) {
        Alert.alert('Error', 'Failed to upload file: ' + error);
      } else {
        const newFile = {
          name: file.name,
          url,
          path,
          size: file.size,
          uploadedAt: new Date().toISOString(),
          uploadedBy: user.uid,
        };
        setUploadedFiles([...uploadedFiles, newFile]);
        Alert.alert('Success', 'File uploaded successfully');
      }
    } catch (error) {
      Alert.alert('Error', 'Failed to upload file');
      console.error('Upload error:', error);
    } finally {
      setUploading(false);
    }
  };

  const handleRequestFeedback = async (file) => {
    if (!aiEnabled || !aiPrompt) {
      Alert.alert('Error', 'AI feedback is not enabled for this widget');
      return;
    }

    console.log('🤖 Requesting AI feedback for:', file.name);
    setGeneratingFeedback(true);
    
    try {
      const fileExtension = fileService.getFileExtension(file.name);
      
      const { feedback: aiFeedback, error: aiError } = await aiService.generateFeedback({
        prompt: aiPrompt,
        fileName: file.name,
        fileType: fileExtension,
        fileUrl: file.url,
        userId: user.uid,
        widgetId: widget.id,
        moduleId,
        courseId,
      });

      console.log('🤖 AI Service Response:', { aiFeedback, aiError });

      if (aiError) {
        console.error('❌ AI feedback error:', aiError);
        Alert.alert('Error', 'Failed to generate feedback: ' + aiError);
      } else if (aiFeedback) {
        console.log('✅ Setting feedback for:', file.name);
        setFeedback(prev => ({
          ...prev,
          [file.name]: aiFeedback,
        }));
        Alert.alert('Success', 'AI feedback generated!');
      } else {
        console.warn('⚠️ No feedback or error returned');
        Alert.alert('Warning', 'No feedback was generated');
      }
    } catch (error) {
      console.error('❌ Unexpected error in handleRequestFeedback:', error);
      Alert.alert('Error', 'An unexpected error occurred: ' + error.message);
    } finally {
      setGeneratingFeedback(false);
    }
  };

  const handleRemoveUploadedFile = async (fileIndex) => {
    const file = uploadedFiles[fileIndex];
    
    try {
      await fileService.deleteFile(file.path);
      setUploadedFiles(uploadedFiles.filter((_, index) => index !== fileIndex));
    } catch (error) {
      Alert.alert('Error', 'Failed to remove file');
      console.error('Remove error:', error);
    }
  };

  return (
    <AnimatedCard style={[styles.widgetCard, { backgroundColor: palette.surface, borderColor: palette.border || 'rgba(255,255,255,0.16)' }]}>
      <View style={styles.widgetHeader}>
        <Text style={[styles.widgetTitle, { color: palette.text || 'white' }]}>
          📁 {widget.title}
        </Text>
        {widget.description && (
          <Text style={[styles.widgetDescription, { color: palette.mutedText || 'rgba(255,255,255,0.7)' }]}>
            {widget.description}
          </Text>
        )}
      </View>

      {/* Downloadable Files */}
      {files.length > 0 && (
        <View style={styles.fileList}>
          {files.map((file, index) => (
            <TouchableOpacity
              key={index}
              style={[
                styles.fileItem,
                { 
                  backgroundColor: 'rgba(255,255,255,0.03)', 
                  borderColor: palette.border || 'rgba(255,255,255,0.16)' 
                }
              ]}
              onPress={() => handleDownload(file)}
              activeOpacity={0.7}
            >
              <View style={[styles.fileIcon, { backgroundColor: palette.primary + '20' }]}>
                <MaterialCommunityIcons
                  name={fileService.getFileIcon(file.name)}
                  size={24}
                  color={palette.primary}
                />
              </View>
              <View style={styles.fileInfo}>
                <Text style={[styles.fileName, { color: palette.text || 'white' }]}>
                  {file.name}
                </Text>
                {file.size && (
                  <Text style={[styles.fileSize, { color: palette.mutedText }]}>
                    {fileService.formatFileSize(file.size)}
                  </Text>
                )}
              </View>
              <MaterialCommunityIcons
                name="download"
                size={24}
                color={palette.primary}
                style={styles.downloadIcon}
              />
            </TouchableOpacity>
          ))}
        </View>
      )}

      {/* Upload Section */}
      {allowUploads && Platform.OS === 'web' && (
        <View style={[styles.uploadSection, { borderColor: palette.border || 'rgba(255,255,255,0.3)' }]}>
          <MaterialCommunityIcons
            name="cloud-upload-outline"
            size={32}
            color={palette.mutedText}
          />
          <Text style={[styles.widgetDescription, { color: palette.mutedText, textAlign: 'center' }]}>
            Upload your completed work
          </Text>
          <label htmlFor={`file-upload-${widget.id}`}>
            <input
              id={`file-upload-${widget.id}`}
              type="file"
              onChange={handleFileUpload}
              style={{ display: 'none' }}
              disabled={uploading}
            />
            <TouchableOpacity
              style={[styles.uploadButton, { backgroundColor: palette.primary }]}
              onPress={() => document.getElementById(`file-upload-${widget.id}`).click()}
              disabled={uploading}
            >
              <MaterialCommunityIcons name="upload" size={18} color="#fff" />
              <Text style={[styles.uploadButtonText, { color: '#fff' }]}>
                {uploading ? 'Uploading...' : 'Choose File'}
              </Text>
            </TouchableOpacity>
          </label>

          {/* Uploaded Files List */}
          {uploadedFiles.length > 0 && (
            <View style={styles.uploadedFilesList}>
              <Text style={[styles.label, { color: palette.text, fontSize: 13, marginBottom: 8 }]}>
                Your Uploads:
              </Text>
              {uploadedFiles.map((file, index) => (
                <View key={index}>
                  <View
                    style={[
                      styles.uploadedFileItem,
                      { backgroundColor: 'rgba(13, 196, 74, 0.1)', borderColor: '#0DC44A' }
                    ]}
                  >
                    <MaterialCommunityIcons
                      name="check-circle"
                      size={18}
                      color="#0DC44A"
                    />
                    <Text style={[styles.uploadedFileName, { color: palette.text }]}>
                      {file.name}
                    </Text>
                    <TouchableOpacity
                      onPress={() => handleRemoveUploadedFile(index)}
                      style={styles.removeFileButton}
                    >
                      <MaterialCommunityIcons name="close" size={18} color={palette.mutedText} />
                    </TouchableOpacity>
                  </View>

                  {/* AI Feedback Button */}
                  {aiEnabled && !feedback[file.name] && (
                    <TouchableOpacity
                      style={[styles.getFeedbackButton, { backgroundColor: palette.primary || '#FF0600' }]}
                      onPress={() => handleRequestFeedback(file)}
                      disabled={generatingFeedback}
                    >
                      <MaterialCommunityIcons name="robot" size={16} color="#fff" />
                      <Text style={[styles.getFeedbackButtonText, { color: '#fff' }]}>
                        {generatingFeedback ? 'Generating...' : 'Get AI Feedback'}
                      </Text>
                    </TouchableOpacity>
                  )}

                  {/* AI Feedback Display */}
                  {feedback[file.name] && (
                    <View style={[styles.feedbackContainer, { 
                      backgroundColor: 'rgba(33, 150, 243, 0.1)', 
                      borderColor: '#2196F3' 
                    }]}>
                      <View style={styles.feedbackHeader}>
                        <MaterialCommunityIcons name="robot" size={20} color="#2196F3" />
                        <Text style={[styles.feedbackTitle, { color: '#2196F3' }]}>
                          AI Feedback
                        </Text>
                      </View>
                      <Text style={[styles.feedbackText, { color: palette.text }]}>
                        {feedback[file.name]}
                      </Text>
                    </View>
                  )}
                </View>
              ))}

              {/* Generating Feedback Indicator */}
              {generatingFeedback && (
                <View style={[styles.feedbackGenerating, { 
                  backgroundColor: 'rgba(255,255,255,0.05)', 
                  borderColor: palette.border 
                }]}>
                  <MaterialCommunityIcons name="robot" size={20} color={palette.primary} />
                  <Text style={[styles.feedbackGeneratingText, { color: palette.mutedText }]}>
                    Generating AI feedback...
                  </Text>
                </View>
              )}
            </View>
          )}
        </View>
      )}

      {files.length === 0 && !allowUploads && (
        <Text style={[styles.widgetDescription, { color: palette.mutedText, textAlign: 'center', marginTop: 20 }]}>
          No files available
        </Text>
      )}
    </AnimatedCard>
  );
};

// Calculator Widget Component
const CalculatorWidget = ({ widget, palette, compact = false }) => {
  const config = widget.config || {};
  const fields = config.fields || [];
  const formulas = config.formulas || [];
  
  const [values, setValues] = useState({});
  const [results, setResults] = useState({});

  useEffect(() => {
    // Calculate results based on formulas
    const newResults = {};
    formulas.forEach(formula => {
      try {
        // Replace field names in formula with values
        let expression = formula.formula;
        fields.forEach(field => {
          const value = values[field.name] || 0;
          expression = expression.replace(new RegExp(field.name, 'g'), value);
        });
        
        // Also replace previous formula results
        formulas.forEach(f => {
          if (f.name !== formula.name && newResults[f.name] !== undefined) {
            expression = expression.replace(new RegExp(f.name, 'g'), newResults[f.name]);
          }
        });
        
        // eslint-disable-next-line no-eval
        const result = eval(expression);
        newResults[formula.name] = result;
      } catch (error) {
        newResults[formula.name] = 'Error';
      }
    });
    setResults(newResults);
  }, [values, fields, formulas]);

  const content = (
    <>
      <View style={styles.widgetHeader}>
        <Text style={[styles.widgetTitle, { color: palette.text || 'white' }]}>
          🧮 {widget.title}
        </Text>
        {widget.description && (
          <Text style={[styles.widgetDescription, { color: palette.mutedText || 'rgba(255,255,255,0.7)' }]}>
            {widget.description}
          </Text>
        )}
      </View>

      <View style={styles.calculatorContainer}>
        {/* Input Fields */}
        {fields.map((field, index) => (
          <View key={`field-${index}`} style={styles.calculatorField}>
            <Text style={[styles.label, { color: palette.text || 'white' }]}>
              {field.label}
            </Text>
            <TextInput
              style={[styles.textInput, { borderColor: palette.border || 'rgba(255,255,255,0.2)', color: palette.text || 'white' }]}
              value={values[field.name]?.toString() || ''}
              onChangeText={(value) => setValues({ ...values, [field.name]: parseFloat(value) || 0 })}
              keyboardType="numeric"
              placeholder="0"
              placeholderTextColor={palette.mutedText || 'rgba(255,255,255,0.4)'}
            />
          </View>
        ))}

        {/* Calculated Results */}
        {formulas.length > 0 && (
          <>
            <View style={{ height: 16 }} />
            {formulas.map((formula, index) => (
              <View key={`formula-${index}`} style={styles.calculatorField}>
                <Text style={[styles.label, { color: palette.text || 'white', fontWeight: '700' }]}>
                  {formula.label}
                </Text>
                <View style={[styles.calculatedResult, { backgroundColor: 'rgba(13, 196, 74, 0.1)', borderColor: '#0DC44A' }]}>
                  <Text style={[styles.calculatedResultText, { color: '#0DC44A' }]}>
                    {typeof results[formula.name] === 'number' 
                      ? (formula.name.includes('roi') || formula.name.includes('percent') || formula.label.toLowerCase().includes('roi') 
                          ? `${results[formula.name].toFixed(2)}%` 
                          : formula.name.includes('Months') || formula.label.toLowerCase().includes('months')
                            ? results[formula.name].toFixed(1)
                            : results[formula.name] < 0
                              ? `-$${Math.abs(results[formula.name]).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`
                              : `$${results[formula.name].toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`)
                      : results[formula.name] || '$0.00'}
                  </Text>
                </View>
              </View>
            ))}
          </>
        )}
      </View>
    </>
  );

  // Don't wrap in AnimatedCard if it's in sidebar/compact mode
  if (compact) {
    return <View>{content}</View>;
  }

  return (
    <AnimatedCard style={[styles.widgetCard, { backgroundColor: palette.surface, borderColor: palette.border || 'rgba(255,255,255,0.16)' }]}>
      {content}
    </AnimatedCard>
  );
};

// Scorecard Widget Component
const ScorecardWidget = ({ widget, palette, onComplete, compact = false }) => {
  const config = widget.config || {};
  const criteria = config.criteria || [];
  const scale = config.scale || { min: 1, max: 5 };
  const thresholds = config.thresholds || [];
  
  const [ratings, setRatings] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = () => {
    const totalScore = Object.values(ratings).reduce((sum, val) => sum + val, 0);
    const maxScore = criteria.length * scale.max;
    const percentage = maxScore > 0 ? (totalScore / maxScore) * 100 : 0;
    
    setSubmitted(true);
    if (onComplete) {
      onComplete({ score: totalScore, maxScore, percentage });
    }
  };

  const totalScore = Object.values(ratings).reduce((sum, val) => sum + val, 0);
  const maxScore = criteria.length * scale.max;
  
  // Find result threshold based on total score
  const getResultThreshold = () => {
    for (let threshold of thresholds) {
      if (totalScore >= threshold.min && totalScore <= threshold.max) {
        return threshold;
      }
    }
    return null;
  };
  
  const resultThreshold = getResultThreshold();

  const content = (
    <>
      <View style={styles.widgetHeader}>
        <Text style={[styles.widgetTitle, { color: palette.text || 'white' }]}>
          ⭐ {widget.title}
        </Text>
        {widget.description && (
          <Text style={[styles.widgetDescription, { color: palette.mutedText || 'rgba(255,255,255,0.7)' }]}>
            {widget.description}
          </Text>
        )}
      </View>

      {!submitted ? (
        <>
          {criteria.map((criterion, index) => (
            <View key={index} style={[styles.scorecardCriterion, { borderBottomColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
              <Text style={[styles.scorecardStatement, { color: palette.text || 'white' }]}>
                {criterion.label || criterion.statement}
              </Text>
              <View style={styles.scorecardScale}>
                {Array.from({ length: scale.max - scale.min + 1 }, (_, i) => i + scale.min).map((score) => (
                  <TouchableOpacity
                    key={score}
                    style={[
                      styles.scoreButton,
                      { borderColor: palette.border || 'rgba(255,255,255,0.2)' },
                      ratings[criterion.key] === score && { backgroundColor: palette.primary || '#FF0600', borderColor: palette.primary }
                    ]}
                    onPress={() => setRatings({ ...ratings, [criterion.key]: score })}
                  >
                    <Text style={[styles.scoreButtonText, { color: ratings[criterion.key] === score ? '#fff' : palette.text }]}>
                      {score}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>
          ))}
          
          <TouchableOpacity
            style={[styles.submitButton, { backgroundColor: palette.primary || '#FF0600', opacity: Object.keys(ratings).length < criteria.length ? 0.5 : 1 }]}
            onPress={handleSubmit}
            disabled={Object.keys(ratings).length < criteria.length}
          >
            <Text style={styles.submitButtonText}>Submit Assessment</Text>
          </TouchableOpacity>
        </>
      ) : (
        <View style={styles.scorecardResults}>
          <Text style={[styles.scorecardResultTitle, { color: resultThreshold?.color || palette.primary }]}>
            Your Score: {totalScore}/{maxScore}
          </Text>
          <Text style={[styles.scorecardResultTitle, { color: resultThreshold?.color || palette.primary, fontSize: 20, marginTop: 8 }]}>
            {resultThreshold?.label || 'Complete'}
          </Text>
          <Text style={[styles.scorecardResultText, { color: palette.mutedText }]}>
            {resultThreshold?.label === 'Ready to Launch' && '🚀 You have what it takes!'}
            {resultThreshold?.label === 'Getting There' && '💪 Keep building these skills!'}
            {resultThreshold?.label === 'Not Ready' && '📚 Focus on development first.'}
          </Text>
          <TouchableOpacity
            onPress={() => { setSubmitted(false); setRatings({}); }}
            style={[styles.retryButton, { backgroundColor: palette.primary || '#FF0600', marginTop: 16 }]}
          >
            <Text style={styles.retryButtonText}>Retake Assessment</Text>
          </TouchableOpacity>
        </View>
      )}
    </>
  );

  if (compact) {
    return <View>{content}</View>;
  }

  return (
    <AnimatedCard style={[styles.widgetCard, { backgroundColor: palette.surface, borderColor: palette.border || 'rgba(255,255,255,0.16)' }]}>
      {content}
    </AnimatedCard>
  );
};

// Checklist Widget Component
const ChecklistWidget = ({ widget, palette, onComplete, compact = false }) => {
  const config = widget.config || {};
  const items = config.items || [];
  
  const [checked, setChecked] = useState({});

  useEffect(() => {
    const checkedCount = Object.values(checked).filter(Boolean).length;
    if (checkedCount === items.length && items.length > 0 && onComplete) {
      onComplete({ completed: checkedCount, total: items.length });
    }
  }, [checked, items.length, onComplete]);

  const toggleCheck = (index) => {
    setChecked({ ...checked, [index]: !checked[index] });
  };

  const checkedCount = Object.values(checked).filter(Boolean).length;
  const progress = items.length > 0 ? (checkedCount / items.length) * 100 : 0;

  const content = (
    <>
      <View style={styles.widgetHeader}>
        <Text style={[styles.widgetTitle, { color: palette.text || 'white' }]}>
          ✅ {widget.title}
        </Text>
        {widget.description && (
          <Text style={[styles.widgetDescription, { color: palette.mutedText || 'rgba(255,255,255,0.7)' }]}>
            {widget.description}
          </Text>
        )}
        <View style={styles.checklistProgress}>
          <Text style={[styles.checklistProgressText, { color: palette.mutedText }]}>
            {checkedCount} / {items.length} completed
          </Text>
          <View style={[styles.progressBar, { backgroundColor: 'rgba(255,255,255,0.1)' }]}>
            <View style={[styles.progressFill, { width: `${progress}%`, backgroundColor: palette.primary || '#FF0600' }]} />
          </View>
        </View>
      </View>

      {items.map((item, index) => (
        <TouchableOpacity
          key={index}
          style={[styles.checklistItem, { borderBottomColor: palette.border || 'rgba(255,255,255,0.1)' }]}
          onPress={() => toggleCheck(index)}
        >
          <View style={[styles.checkbox, { borderColor: palette.border || 'rgba(255,255,255,0.3)', backgroundColor: checked[index] ? (palette.primary || '#FF0600') : 'transparent' }]}>
            {checked[index] && (
              <MaterialCommunityIcons name="check" size={16} color="#fff" />
            )}
          </View>
          <View style={{ flex: 1 }}>
            <Text style={[styles.checklistItemText, { color: palette.text || 'white', textDecorationLine: checked[index] ? 'line-through' : 'none', opacity: checked[index] ? 0.6 : 1 }]}>
              {item.label || item.text}
            </Text>
          </View>
        </TouchableOpacity>
      ))}
    </>
  );

  if (compact) {
    return <View>{content}</View>;
  }

  return (
    <AnimatedCard style={[styles.widgetCard, { backgroundColor: palette.surface, borderColor: palette.border || 'rgba(255,255,255,0.16)' }]}>
      {content}
    </AnimatedCard>
  );
};

// Decision Tree Widget Component
const DecisionTreeWidget = ({ widget, palette, onComplete, compact = false }) => {
  const config = widget.config || {};
  const nodes = config.nodes || [];
  
  const [currentNodeId, setCurrentNodeId] = useState('start');
  const [path, setPath] = useState([]);
  const [result, setResult] = useState(null);

  // Find node by ID
  const getCurrentNode = () => nodes.find(n => n.id === currentNodeId);

  const handleDecision = (choice) => {
    const currentNode = getCurrentNode();
    if (!currentNode) return;
    
    const nextNodeId = choice === 'yes' ? currentNode.yes : currentNode.no;
    const nextNode = nodes.find(n => n.id === nextNodeId);
    
    setPath([...path, { 
      question: currentNode.question, 
      answer: choice 
    }]);
    
    // Check if next node has a result (terminal) or another question
    if (nextNode && nextNode.result) {
      setResult(nextNode.result);
      if (onComplete) {
        onComplete({ decisions: path.length + 1 });
      }
    } else if (nextNode && nextNode.question) {
      setCurrentNodeId(nextNodeId);
    } else {
      // Fallback: if no node found, show the nextNodeId as result
      setResult(nextNodeId || 'Decision path complete.');
      if (onComplete) {
        onComplete({ decisions: path.length + 1 });
      }
    }
  };

  const reset = () => {
    setCurrentNodeId('start');
    setPath([]);
    setResult(null);
  };

  if (!nodes || nodes.length === 0) {
    return (
      <AnimatedCard style={[styles.widgetCard, { backgroundColor: palette.surface, borderColor: palette.border || 'rgba(255,255,255,0.16)' }]}>
        <Text style={[styles.widgetTitle, { color: palette.text }]}>
          🌳 {widget.title}
        </Text>
        <Text style={[styles.widgetDescription, { color: palette.mutedText }]}>
          No decision nodes configured.
        </Text>
      </AnimatedCard>
    );
  }

  return (
    <AnimatedCard style={[styles.widgetCard, { backgroundColor: palette.surface, borderColor: palette.border || 'rgba(255,255,255,0.16)' }]}>
      <View style={styles.widgetHeader}>
        <Text style={[styles.widgetTitle, { color: palette.text || 'white' }]}>
          🌳 {widget.title}
        </Text>
        {widget.description && (
          <Text style={[styles.widgetDescription, { color: palette.mutedText || 'rgba(255,255,255,0.7)' }]}>
            {widget.description}
          </Text>
        )}
      </View>

      {!result ? (
        <View style={styles.decisionTreeContainer}>
          <View style={[styles.decisionTreeNode, { backgroundColor: 'rgba(255,255,255,0.05)', borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
            <Text style={[styles.decisionTreeQuestion, { color: palette.text || 'white' }]}>
              {getCurrentNode()?.question}
            </Text>
            <View style={styles.decisionTreeButtons}>
              <TouchableOpacity
                style={[styles.decisionButton, styles.yesButton, { backgroundColor: '#0DC44A' }]}
                onPress={() => handleDecision('yes')}
              >
                <Text style={styles.decisionButtonText}>Yes</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.decisionButton, styles.noButton, { backgroundColor: '#EA806B' }]}
                onPress={() => handleDecision('no')}
              >
                <Text style={styles.decisionButtonText}>No</Text>
              </TouchableOpacity>
            </View>
          </View>
          
          {path.length > 0 && (
            <View style={styles.decisionPath}>
              <Text style={[styles.decisionPathTitle, { color: palette.mutedText }]}>Your Path:</Text>
              {path.map((step, index) => (
                <Text key={index} style={[styles.decisionPathStep, { color: palette.text }]}>
                  {index + 1}. {step.answer === 'yes' ? '✓' : '✗'}
                </Text>
              ))}
            </View>
          )}
        </View>
      ) : (
        <View style={styles.decisionTreeResult}>
          <Text style={[styles.decisionTreeResultTitle, { color: '#0DC44A' }]}>
            📍 Recommendation
          </Text>
          <Text style={[styles.decisionTreeResultText, { color: palette.text }]}>
            {result}
          </Text>
          <TouchableOpacity
            onPress={reset}
            style={[styles.retryButton, { backgroundColor: palette.primary || '#FF0600' }]}
          >
            <Text style={styles.retryButtonText}>Start Over</Text>
          </TouchableOpacity>
        </View>
      )}
    </AnimatedCard>
  );
};

// Timeline Widget Component
const TimelineWidget = ({ widget, palette, compact = false }) => {
  const config = widget.config || {};
  const milestones = (config.milestones || []).sort((a, b) => (a.month || 0) - (b.month || 0));

  return (
    <AnimatedCard style={[styles.widgetCard, { backgroundColor: palette.surface, borderColor: palette.border || 'rgba(255,255,255,0.16)' }]}>
      <View style={styles.widgetHeader}>
        <Text style={[styles.widgetTitle, { color: palette.text || 'white' }]}>
          📅 {widget.title}
        </Text>
        {widget.description && (
          <Text style={[styles.widgetDescription, { color: palette.mutedText || 'rgba(255,255,255,0.7)' }]}>
            {widget.description}
          </Text>
        )}
      </View>

      <View style={styles.timelineContainer}>
        {milestones.map((milestone, index) => (
          <View key={index} style={styles.timelineItem}>
            <View style={styles.timelineDot}>
              <View style={[styles.timelineDotInner, { backgroundColor: palette.primary || '#FF0600' }]} />
              {index < milestones.length - 1 && (
                <View style={[styles.timelineLine, { backgroundColor: palette.border || 'rgba(255,255,255,0.2)' }]} />
              )}
            </View>
            <View style={[styles.timelineContent, { backgroundColor: 'rgba(255,255,255,0.05)', borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
              <Text style={[styles.timelineMonth, { color: palette.primary || '#FF0600' }]}>
                Month {milestone.month}
              </Text>
              <Text style={[styles.timelineTitle, { color: palette.text || 'white' }]}>
                {milestone.title}
              </Text>
              {milestone.description && (
                <Text style={[styles.timelineDescription, { color: palette.mutedText }]}>
                  {milestone.description}
                </Text>
              )}
            </View>
          </View>
        ))}
      </View>
    </AnimatedCard>
  );
};

// Comparison Widget Component
const ComparisonWidget = ({ widget, palette, compact = false }) => {
  const config = widget.config || {};
  const options = config.options || [];
  const criteriaList = config.criteriaList || [];

  const [userScores, setUserScores] = useState({});

  const handleScoreChange = (optionIndex, criterion, value) => {
    setUserScores({
      ...userScores,
      [`${optionIndex}-${criterion}`]: value
    });
  };

  // Show empty state if no data
  if (options.length === 0 || criteriaList.length === 0) {
    return (
      <AnimatedCard style={[styles.widgetCard, { backgroundColor: palette.surface, borderColor: palette.border || 'rgba(255,255,255,0.16)' }]}>
        <View style={styles.widgetHeader}>
          <Text style={[styles.widgetTitle, { color: palette.text || 'white' }]}>
            ⚖️ {widget.title}
          </Text>
          {widget.description && (
            <Text style={[styles.widgetDescription, { color: palette.mutedText || 'rgba(255,255,255,0.7)' }]}>
              {widget.description}
            </Text>
          )}
        </View>
        <View style={[styles.emptyWidgetState, { backgroundColor: 'rgba(255,255,255,0.03)' }]}>
          <Text style={[styles.emptyWidgetText, { color: palette.mutedText || 'rgba(255,255,255,0.5)' }]}>
            This comparison widget hasn't been configured yet. {options.length === 0 ? 'Add options ' : ''}{criteriaList.length === 0 ? 'Add criteria' : ''} in the admin panel.
          </Text>
        </View>
      </AnimatedCard>
    );
  }

  return (
    <AnimatedCard style={[styles.widgetCard, { backgroundColor: palette.surface, borderColor: palette.border || 'rgba(255,255,255,0.16)' }]}>
      <View style={styles.widgetHeader}>
        <Text style={[styles.widgetTitle, { color: palette.text || 'white' }]}>
          ⚖️ {widget.title}
        </Text>
        {widget.description && (
          <Text style={[styles.widgetDescription, { color: palette.mutedText || 'rgba(255,255,255,0.7)' }]}>
            {widget.description}
          </Text>
        )}
      </View>

      <ScrollView horizontal style={styles.comparisonScroll}>
        <View style={styles.comparisonTable}>
          {/* Header Row */}
          <View style={[styles.comparisonRow, { borderBottomColor: palette.border || 'rgba(255,255,255,0.2)' }]}>
            <View style={[styles.comparisonCell, styles.comparisonHeaderCell, { minWidth: 150 }]}>
              <Text style={[styles.comparisonHeaderText, { color: palette.text || 'white' }]}>Criteria</Text>
            </View>
            {options.map((option, index) => (
              <View key={index} style={[styles.comparisonCell, styles.comparisonHeaderCell, { minWidth: 150 }]}>
                <Text style={[styles.comparisonHeaderText, { color: palette.text || 'white' }]}>{option.name || `Option ${index + 1}`}</Text>
              </View>
            ))}
          </View>

          {/* Criteria Rows */}
          {criteriaList.map((criterion, criterionIndex) => (
            <View key={criterionIndex} style={[styles.comparisonRow, { borderBottomColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
              <View style={[styles.comparisonCell, { minWidth: 150, backgroundColor: 'rgba(255,255,255,0.02)' }]}>
                <Text style={[styles.comparisonCriterionText, { color: palette.text || 'white' }]}>{criterion}</Text>
              </View>
              {options.map((option, optionIndex) => (
                <View key={optionIndex} style={[styles.comparisonCell, { minWidth: 150 }]}>
                  <TextInput
                    style={[styles.comparisonInput, { borderColor: palette.border || 'rgba(255,255,255,0.2)', color: palette.text || 'white' }]}
                    value={userScores[`${optionIndex}-${criterion}`] || ''}
                    onChangeText={(value) => handleScoreChange(optionIndex, criterion, value)}
                    placeholder="Rate"
                    placeholderTextColor={palette.mutedText || 'rgba(255,255,255,0.4)'}
                    multiline
                  />
                </View>
              ))}
            </View>
          ))}
        </View>
      </ScrollView>
    </AnimatedCard>
  );
};

// Sequential Quiz Widget Component
const SequentialQuizWidget = ({ widget, palette, onComplete, compact = false }) => {
  const config = widget.config || {};
  const questions = config.questions || [];
  const options = config.options || [];
  
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  const [score, setScore] = useState(0);
  const [completed, setCompleted] = useState(false);

  const currentQuestion = questions[currentQuestionIndex];
  const isLastQuestion = currentQuestionIndex === questions.length - 1;

  const handleSelectAnswer = (answerIndex) => {
    if (showFeedback) return; // Can't change after submitting
    setSelectedAnswer(answerIndex);
  };

  const handleSubmitAnswer = () => {
    if (selectedAnswer === null) return;

    const correct = selectedAnswer === currentQuestion.correctAnswer;
    setIsCorrect(correct);
    setShowFeedback(true);
    
    if (correct) {
      setScore(score + 1);
    }
  };

  const handleNextQuestion = () => {
    if (isLastQuestion) {
      setCompleted(true);
      if (onComplete) {
        onComplete({ score, total: questions.length, percentage: ((score + (isCorrect ? 1 : 0)) / questions.length) * 100 });
      }
    } else {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      setSelectedAnswer(null);
      setShowFeedback(false);
      setIsCorrect(false);
    }
  };

  const handleReset = () => {
    setCurrentQuestionIndex(0);
    setSelectedAnswer(null);
    setShowFeedback(false);
    setIsCorrect(false);
    setScore(0);
    setCompleted(false);
  };

  const content = (
    <>
      <View style={styles.widgetHeader}>
        <Text style={[styles.widgetTitle, { color: palette.text || 'white' }]}>
          🎯 {widget.title}
        </Text>
        {widget.description && (
          <Text style={[styles.widgetDescription, { color: palette.mutedText || 'rgba(255,255,255,0.7)' }]}>
            {widget.description}
          </Text>
        )}
      </View>

      {!completed ? (
        <View style={styles.sequentialQuizContainer}>
          {/* Progress */}
          <View style={styles.quizProgress}>
            <Text style={[styles.quizProgressText, { color: palette.mutedText }]}>
              Question {currentQuestionIndex + 1} of {questions.length}
            </Text>
            <View style={[styles.progressBar, { backgroundColor: 'rgba(255,255,255,0.1)' }]}>
              <View style={[styles.progressFill, { width: `${((currentQuestionIndex + 1) / questions.length) * 100}%`, backgroundColor: palette.primary || '#FF0600' }]} />
            </View>
          </View>

          {/* Question */}
          <View style={[styles.sequentialQuestionCard, { backgroundColor: 'rgba(255,255,255,0.05)', borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
            <Text style={[styles.sequentialQuestionText, { color: palette.text || 'white' }]}>
              {currentQuestion?.question}
            </Text>
          </View>

          {/* Answer Options */}
          <View style={styles.sequentialOptionsContainer}>
            {options.map((option, index) => {
              const isSelected = selectedAnswer === index;
              const isCorrectAnswer = currentQuestion.correctAnswer === index;
              const showCorrect = showFeedback && isCorrectAnswer;
              const showWrong = showFeedback && isSelected && !isCorrect;

              return (
                <TouchableOpacity
                  key={index}
                  style={[
                    styles.sequentialOption,
                    { borderColor: palette.border || 'rgba(255,255,255,0.2)' },
                    isSelected && !showFeedback && { backgroundColor: 'rgba(255, 6, 0, 0.2)', borderColor: palette.primary },
                    showCorrect && { backgroundColor: 'rgba(13, 196, 74, 0.2)', borderColor: '#0DC44A' },
                    showWrong && { backgroundColor: 'rgba(234, 128, 107, 0.2)', borderColor: '#EA806B' },
                  ]}
                  onPress={() => handleSelectAnswer(index)}
                  disabled={showFeedback}
                >
                  <Text style={[styles.sequentialOptionText, { color: palette.text || 'white' }]}>
                    {option}
                  </Text>
                  {showCorrect && <Text style={styles.correctIcon}>✓</Text>}
                  {showWrong && <Text style={styles.wrongIcon}>✗</Text>}
                </TouchableOpacity>
              );
            })}
          </View>

          {/* Feedback & Actions */}
          {!showFeedback ? (
            <TouchableOpacity
              style={[styles.submitButton, { backgroundColor: palette.primary || '#FF0600', opacity: selectedAnswer === null ? 0.5 : 1 }]}
              onPress={handleSubmitAnswer}
              disabled={selectedAnswer === null}
            >
              <Text style={styles.submitButtonText}>Submit Answer</Text>
            </TouchableOpacity>
          ) : (
            <View style={styles.sequentialFeedback}>
              <Text style={[styles.sequentialFeedbackText, { color: isCorrect ? '#0DC44A' : '#EA806B' }]}>
                {isCorrect ? '✓ Correct!' : '✗ Incorrect'}
              </Text>
              <TouchableOpacity
                style={[styles.submitButton, { backgroundColor: palette.primary || '#FF0600', marginTop: 12 }]}
                onPress={handleNextQuestion}
              >
                <Text style={styles.submitButtonText}>{isLastQuestion ? 'See Results' : 'Next Question'}</Text>
              </TouchableOpacity>
            </View>
          )}
        </View>
      ) : (
        <View style={styles.sequentialResults}>
          <Text style={[styles.sequentialResultTitle, { color: (score / questions.length) >= 0.7 ? '#0DC44A' : (score / questions.length) >= 0.5 ? '#FFC107' : '#EA806B' }]}>
            Your Score: {score}/{questions.length}
          </Text>
          <Text style={[styles.sequentialResultPercentage, { color: palette.text }]}>
            {Math.round((score / questions.length) * 100)}% Correct
          </Text>
          <Text style={[styles.sequentialResultText, { color: palette.mutedText }]}>
            {(score / questions.length) >= 0.8 ? '🎉 Excellent work!' : (score / questions.length) >= 0.6 ? '👍 Good job!' : '💪 Keep practicing!'}
          </Text>
          <TouchableOpacity
            onPress={handleReset}
            style={[styles.retryButton, { backgroundColor: palette.primary || '#FF0600', marginTop: 16 }]}
          >
            <Text style={styles.retryButtonText}>Try Again</Text>
          </TouchableOpacity>
        </View>
      )}
    </>
  );

  if (compact) {
    return <View>{content}</View>;
  }

  return (
    <AnimatedCard style={[styles.widgetCard, { backgroundColor: palette.surface, borderColor: palette.border || 'rgba(255,255,255,0.16)' }]}>
      {content}
    </AnimatedCard>
  );
};

export default WidgetRenderer;

